import { useState } from "react"
import Link from "next/link"
import { useMutation, useQuery } from "react-apollo"
import classNames from "classnames"
import { LOCAL_TOGGLE_PREMIUM_COFOUNDER } from "../../apollo-state/graphql/mutations"
import convertProfilePicUrl from "../../utils/convert-profile-pic-url"
import {
  PROMPTS,
  SKILL_LABELS,
  checklist,
  displayName,
  fitLines,
  hostOf,
  hoursLabel,
  ideaStageLabel,
  industryLabel,
  intentLines,
  isStockText,
  linkify,
  paragraphs,
  promptLabel,
  promptsOf,
  realText,
  skillWords,
  startupStageLabel,
  wordCount,
} from "./lib"
import { SIMILAR_FOUNDERS_QUERY } from "./queries"
import { IconArrow, IconCheck, IconChevron, IconEyeOff, IconHeart, IconMessage, IconPlus } from "./icons"
import { EditButton, Pill } from "./ui"

const COLLAPSE_AT = 140 // words; the collapsed view shows about 120

const Linked = ({ text }) =>
  linkify(text).map((part, i) =>
    typeof part === "string" ? (
      part
    ) : (
      <a key={i} className="fp-inline-link" href={part.url} target="_blank" rel="noopener noreferrer nofollow ugc">
        {part.text}
      </a>
    )
  )

// Paragraph lines, with "Read more" for long text
const Prose = ({ text, collapse = true }) => {
  const paras = paragraphs(text)
  const long = collapse && wordCount(text) > COLLAPSE_AT
  const [open, setOpen] = useState(false)
  let shown = paras
  if (long && !open) {
    shown = []
    let left = 120
    for (const lines of paras) {
      if (left <= 0) break
      const words = lines.join(" ").split(/\s+/)
      if (words.length <= left) {
        shown.push(lines)
        left -= words.length
      } else {
        shown.push([`${words.slice(0, left).join(" ").replace(/[\s,;:.]+$/, "")}…`])
        left = 0
      }
    }
  }
  return (
    <>
      {shown.map((lines, i) => (
        <p className="fp-prose" key={i}>
          {lines.map((l, j) => (
            <span key={j}>
              {j > 0 && <br />}
              <Linked text={l} />
            </span>
          ))}
        </p>
      ))}
      {long && (
        <button type="button" className="fp-text-btn" aria-expanded={open ? "true" : "false"} onClick={() => setOpen(!open)}>
          {open ? "Show less" : "Read more"}
        </button>
      )}
    </>
  )
}

const SectionHead = ({ id, title, onEdit, editLabel }) => (
  <div className="fp-section-head">
    <h2 className="fp-h2" id={id}>
      {title}
    </h2>
    {onEdit && <EditButton onClick={onEdit} label={editLabel} />}
  </div>
)

// ---- About ----

export const AboutSection = ({ user, owner, onEdit }) => {
  const text = realText(user.whyMakeGoodCofounder)
  if (!text) {
    if (!owner) return null
    return (
      <section className="fp-card fp-slot" aria-labelledby="fp-about">
        <h2 className="fp-h2" id="fp-about">
          About you
        </h2>
        <p className="fp-prose">
          {isStockText(user.whyMakeGoodCofounder)
            ? "Your About still has the line the old sign-up filled in for you. Visitors don't see it. A few sentences on what you've built and who you want to build with goes a long way."
            : "Founders read this first. A few sentences on what you've built and who you want to build with goes a long way."}
        </p>
        <div>
          <button type="button" className="fp-btn" onClick={onEdit}>
            Write your About
          </button>
        </div>
      </section>
    )
  }
  return (
    <section className="fp-card" aria-labelledby="fp-about">
      <SectionHead id="fp-about" title={owner ? "About you" : "About"} onEdit={owner && onEdit} />
      <Prose text={text} />
    </section>
  )
}

// ---- Prompts ----

export const PromptsSection = ({ user, owner, onEdit }) => {
  const prompts = promptsOf(user)
  if (!prompts.length) {
    if (!owner) return null
    return (
      <section className="fp-card fp-slot" aria-labelledby="fp-prompts">
        <h2 className="fp-h2" id="fp-prompts">
          Prompts
        </h2>
        <p className="fp-prose fp-muted">Short answers people read first. Pick up to three.</p>
        <div className="fp-chips">
          {PROMPTS.slice(0, 3).map((p) => (
            <button type="button" key={p.id} className="fp-chip" onClick={() => onEdit(p.id)}>
              {p.label}
            </button>
          ))}
          <button type="button" className="fp-text-btn" onClick={() => onEdit(null)}>
            More prompts
          </button>
        </div>
      </section>
    )
  }
  return (
    <section className="fp-stack" aria-label={owner ? "Your prompts" : `${displayName(user.firstName)}'s answers to prompts`}>
      {owner && (
        <div className="fp-section-head fp-section-head--bare">
          <h2 className="fp-h2">Prompts</h2>
          <EditButton onClick={() => onEdit(null)} />
        </div>
      )}
      {prompts.map((p) => (
        <article className="fp-card fp-prompt" key={p.id}>
          <h3 className="fp-prompt-q">{promptLabel(p.id)}</h3>
          <p className="fp-prompt-a">{p.answer}</p>
        </article>
      ))}
    </section>
  )
}

// ---- What they're building ----

const IdeaPills = ({ idea }) => {
  const industry = industryLabel(idea.ideaIndustry)
  const stage = ideaStageLabel(idea.ideaStage)
  if (!industry && !stage) return null
  return (
    <div className="fp-pills">
      {industry && <Pill tone="amber">{industry}</Pill>}
      {stage && <Pill tone="blue">{stage}</Pill>}
    </div>
  )
}

const sameText = (a, b) => realText(a).replace(/\s+/g, " ").toLowerCase() === realText(b).replace(/\s+/g, " ").toLowerCase()

export const BuildingSection = ({ user, owner, ideas, onEditIdea, onNamePitch, onNewIdea, onToggleIdea, toggling }) => {
  const name = displayName(user.firstName)
  const list = owner ? ideas : ideas.filter((i) => i.isActive)
  const pitch = user.hasIdeaForStartup ? realText(user.elevatorPitchForStartup) : ""
  const [more, setMore] = useState(false)
  const heading = owner ? "What you're building" : `What ${name} is building`

  if (!list.length && !pitch) {
    if (!owner || !user.hasIdeaForStartup) return null
    return (
      <section className="fp-card fp-slot" aria-labelledby="fp-building">
        <h2 className="fp-h2" id="fp-building">
          {heading}
        </h2>
        <p className="fp-prose fp-muted">Post your idea so founders can like it, comment and message you about it.</p>
        <div>
          <button type="button" className="fp-btn" onClick={onNewIdea}>
            Post your idea
          </button>
        </div>
      </section>
    )
  }

  if (!list.length) {
    // the pitch from the old sign-up, never posted as an idea
    const legacy = { ideaStage: user.ideaStage, ideaIndustry: user.ideaIndustry }
    return (
      <section className="fp-stack" aria-labelledby="fp-building">
        <SectionHead id="fp-building" title={heading} />
        <article className="fp-card">
          <h3 className="fp-prompt-q">{owner ? "Your idea, from sign-up" : "The idea"}</h3>
          <Prose text={pitch} />
          <IdeaPills idea={legacy} />
          {owner && (
            <div className="fp-callout">
              <p>It has no name yet, so it isn't in the Ideas feed. Name it and post it to get likes and comments.</p>
              <button type="button" className="fp-btn" onClick={onNamePitch}>
                Name and post it
              </button>
            </div>
          )}
        </article>
      </section>
    )
  }

  const [main, ...rest] = list
  const longer = pitch && !sameText(pitch, main.elevatorPitch) && wordCount(pitch) > wordCount(main.elevatorPitch)
  return (
    <section className="fp-stack" aria-labelledby="fp-building">
      <div className="fp-section-head fp-section-head--bare">
        <h2 className="fp-h2" id="fp-building">
          {heading}
        </h2>
        {owner && (
          <button type="button" className="fp-edit" onClick={onNewIdea}>
            <IconPlus />
            <span>Post an idea</span>
          </button>
        )}
      </div>
      <article className={classNames("fp-card fp-idea", { "fp-idea--hidden": !main.isActive })}>
        <div className="fp-row fp-row--between fp-row--top">
          <h3 className="fp-idea-title">{main.title}</h3>
          {owner && <EditButton onClick={() => onEditIdea(main)} />}
        </div>
        {!main.isActive && <HiddenNote />}
        <Prose text={main.elevatorPitch} />
        {longer && (
          <>
            <button type="button" className="fp-text-btn" aria-expanded={more ? "true" : "false"} onClick={() => setMore(!more)}>
              {more ? "Less about the idea" : "More about the idea"}
            </button>
            {more && <Prose text={pitch} collapse={false} />}
          </>
        )}
        <IdeaPills idea={main} />
        <div className="fp-idea-foot">
          <span className="fp-meta-item">
            <IconHeart />
            {main.totalLikes || 0} {main.totalLikes === 1 ? "like" : "likes"}
          </span>
          <span className="fp-meta-item">
            <IconMessage size={16} />
            {main.totalComments || 0} {main.totalComments === 1 ? "comment" : "comments"}
          </span>
          <span className="fp-grow" />
          {owner && (
            <button type="button" className="fp-text-btn" disabled={toggling} onClick={() => onToggleIdea(main)}>
              {main.isActive ? "Hide it" : "Post it"}
            </button>
          )}
          {main.isActive && (
            <Link href="/ideas/[id]" as={`/ideas/${main.id}`}>
              <a className="fp-link-strong">
                Open the idea <IconArrow />
              </a>
            </Link>
          )}
        </div>
      </article>
      {rest.map((idea) => (
        <div className={classNames("fp-card fp-idea-row", { "fp-idea--hidden": !idea.isActive })} key={idea.id}>
          <div className="fp-grow">
            {idea.isActive ? (
              <Link href="/ideas/[id]" as={`/ideas/${idea.id}`}>
                <a className="fp-idea-row-title">{idea.title}</a>
              </Link>
            ) : (
              <span className="fp-idea-row-title">{idea.title}</span>
            )}
            <span className="fp-muted fp-small">
              {[idea.isActive ? "Also posted" : "Hidden · only you can see this", ideaStageLabel(idea.ideaStage)].filter(Boolean).join(" · ")}
            </span>
          </div>
          {owner ? (
            <div className="fp-row">
              <button type="button" className="fp-text-btn" disabled={toggling} onClick={() => onToggleIdea(idea)}>
                {idea.isActive ? "Hide" : "Post"}
              </button>
              <EditButton onClick={() => onEditIdea(idea)} hidden label={`Edit ${idea.title}`} />
            </div>
          ) : (
            <IconChevron />
          )}
        </div>
      ))}
    </section>
  )
}

const HiddenNote = () => (
  <p className="fp-hidden-note">
    <IconEyeOff />
    Hidden. Only you can see this idea.
  </p>
)

// ---- The Nest ----

export const NestSection = ({ answers }) => {
  if (!answers || !answers.length) return null
  return (
    <section className="fp-stack" aria-labelledby="fp-nest">
      <SectionHead id="fp-nest" title="Answers in the Nest" />
      <div className="fp-card fp-list">
        {answers.map((a) => (
          <Link href="/the-nest/q/[slug]" as={`/the-nest/q/${a.questionSlug}`} key={a.id}>
            <a className="fp-list-item">
              <span className="fp-list-title">{a.questionTitle}</span>
              <span className="fp-prose fp-clamp">“{paragraphs(a.body).map((p) => p.join(" ")).join(" ")}”</span>
            </a>
          </Link>
        ))}
      </div>
    </section>
  )
}

// ---- The basics ----

export const BasicsCard = ({ user, owner, onEdit }) => {
  const brings = (user.hasSkills || []).map((s) => SKILL_LABELS[s.itemId]).filter(Boolean)
  const needs = (user.wantsSkills || []).map((s) => SKILL_LABELS[s.itemId]).filter(Boolean)
  const hours = hoursLabel(user.hoursPerWeekAvailable)
  const here = intentLines(user, { own: owner })
  const startupBits = user.hasOwnStartup
    ? [startupStageLabel(user.startupStage), industryLabel(user.startupIndustry)].filter(Boolean)
    : []
  const startupUrl = user.hasOwnStartup && user.ownStartupUrl
  if (!owner && !brings.length && !needs.length && !hours && !here.length) return null
  return (
    <section className="fp-card" aria-labelledby="fp-basics">
      <SectionHead id="fp-basics" title="The basics" onEdit={owner && onEdit} />
      <dl className="fp-dl">
        {brings.length > 0 && (
          <div>
            <dt>{owner ? "You bring" : "Brings"}</dt>
            <dd className="fp-pills">
              {brings.map((s) => (
                <Pill tone="blue" key={s}>
                  {s}
                </Pill>
              ))}
            </dd>
          </div>
        )}
        {needs.length > 0 && (
          <div>
            <dt>{owner ? "You're looking for" : "Looking for"}</dt>
            <dd className="fp-pills">
              {needs.map((s) => (
                <Pill tone="coral" key={s}>
                  {s}
                </Pill>
              ))}
            </dd>
          </div>
        )}
        {hours && (
          <div>
            <dt>Time</dt>
            <dd>{hours}</dd>
          </div>
        )}
        {here.length > 0 && (
          <div>
            <dt>Here to</dt>
            <dd>
              {here.map((l, i) => (
                <span className="fp-block" key={i}>
                  {l}
                </span>
              ))}
            </dd>
          </div>
        )}
        {(startupBits.length > 0 || startupUrl) && (
          <div>
            <dt>{owner ? "Your startup" : "Startup"}</dt>
            <dd>
              {startupUrl && (
                <a className="fp-link-strong fp-block" href={startupUrl} target="_blank" rel="noopener noreferrer nofollow">
                  {hostOf(startupUrl)}
                </a>
              )}
              {startupBits.length > 0 && <span className="fp-block">{startupBits.join(" · ")}</span>}
            </dd>
          </div>
        )}
      </dl>
      {owner && !brings.length && (
        <button type="button" className="fp-btn fp-btn--outline" onClick={onEdit}>
          Add the basics
        </button>
      )}
    </section>
  )
}

// ---- "You and {name}" ----

export const FitCard = ({ me, user }) => {
  const lines = fitLines(me, user)
  if (!lines.length) return null
  return (
    <section className="fp-card fp-fit" aria-labelledby="fp-fit">
      <h2 className="fp-h2" id="fp-fit">
        You and {displayName(user.firstName)}
      </h2>
      <ul className="fp-fit-list">
        {lines.map((l) => (
          <li key={l}>
            <IconCheck color="#047857" />
            <span>{l}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

export const JoinCard = ({ user }) => (
  <section className="fp-card fp-fit" aria-labelledby="fp-join">
    <h2 className="fp-h2" id="fp-join">
      Want to talk to {displayName(user.firstName)}?
    </h2>
    <p className="fp-prose fp-muted fp-grow">Join free to message founders, save the ones you like and post your own idea.</p>
    <Link href="/sign-up">
      <a className="fp-btn">Join free</a>
    </Link>
  </section>
)

// Signed-out visitors see the shape of a profile, not its content: the API
// doesn't send what's below the lock (backend utils/profile-visibility.js), so
// these are blank lines for the blur to cover.
const SkeletonCard = ({ lines }) => (
  <section className="fp-card">
    <span className="fp-skeleton fp-skeleton--title" />
    {lines.map((width, i) => (
      <span className="fp-skeleton" style={{ width: `${width}%` }} key={i} />
    ))}
  </section>
)

export const LockedPreview = () => (
  <>
    <div className="fp-main">
      <SkeletonCard lines={[100, 96, 100, 88, 60]} />
      <SkeletonCard lines={[92, 100, 70]} />
      <SkeletonCard lines={[100, 84, 96, 50]} />
    </div>
    <aside className="fp-side">
      <SkeletonCard lines={[70, 55, 80, 60]} />
    </aside>
  </>
)

// ---- More founders like them ----

const similarHeading = (data, user) => {
  const words = data && skillWords(data.skillItemId)
  if (!words) return `More founders like ${displayName(user.firstName)}`
  const plural = words.plural.charAt(0).toUpperCase() + words.plural.slice(1)
  if (data.intent === "join") return `More ${words.plural} who want to join an idea`
  if (data.intent === "idea") return `More ${words.plural} with an idea of their own`
  return `${plural} you might also like`
}

// the skill they were matched on, else their first one
const cardSkill = (founder, itemId) => {
  const ids = (founder.hasSkills || []).map((s) => s.itemId)
  return SKILL_LABELS[ids.includes(itemId) ? itemId : ids[0]]
}

export const SimilarSection = ({ user }) => {
  const { data } = useQuery(SIMILAR_FOUNDERS_QUERY, { variables: { id: user.id }, ssr: false })
  const result = data && data.similarFounders
  if (!result || !result.founders.length) return null
  return (
    <section className="fp-stack" aria-labelledby="fp-similar">
      <div className="fp-section-head fp-section-head--bare">
        <h2 className="fp-h2" id="fp-similar">
          {similarHeading(result, user)}
        </h2>
        <Link href="/find-a-co-founder">
          <a className="fp-link-strong">See all</a>
        </Link>
      </div>
      <div className="fp-similar">
        {result.founders.map((f) => (
          <Link href="/profile/[id]" as={`/profile/${f.id}`} key={f.id}>
            <a className="fp-card fp-similar-card">
              <img src={convertProfilePicUrl(f.profilePic, 44, 44)} alt="" width="44" height="44" />
              <span className="fp-grow">
                <span className="fp-similar-name">{displayName(f.firstName)}</span>
                <span className="fp-muted fp-small">
                  {[cardSkill(f, result.skillItemId), String(f.location || "").split(",")[0]].filter(Boolean).join(" · ")}
                </span>
              </span>
            </a>
          </Link>
        ))}
      </div>
    </section>
  )
}

// ---- owner-only cards ----

export const FinishCard = ({ me, onAction }) => {
  const items = checklist(me)
  const done = items.filter((i) => i.done).length
  if (done === items.length) return null
  return (
    <section className="fp-card" aria-labelledby="fp-finish">
      <h2 className="fp-h3" id="fp-finish">
        Finish your page
      </h2>
      <div className="fp-progress" role="progressbar" aria-label="Page finished" aria-valuemin={0} aria-valuemax={items.length} aria-valuenow={done}>
        <div style={{ width: `${(done / items.length) * 100}%` }} />
      </div>
      <ul className="fp-checklist">
        {items.map((i) =>
          i.done ? (
            <li key={i.key} className="fp-check-done">
              <IconCheck color="#047857" />
              {i.label}
            </li>
          ) : (
            <li key={i.key}>
              <button type="button" onClick={() => onAction(i.key)}>
                <span className="fp-check-ring" aria-hidden="true" />
                {i.label}
              </button>
            </li>
          )
        )}
      </ul>
    </section>
  )
}

export const VisibilityCard = ({ me }) => {
  const [togglePremium] = useMutation(LOCAL_TOGGLE_PREMIUM_COFOUNDER, {
    variables: { message: "Premium lists your page in Find a co-founder." },
  })
  const listed = me.profileAccepted && me.fitForFounderGrid
  return (
    <section className="fp-card" aria-labelledby="fp-seen">
      <h2 className="fp-h3" id="fp-seen">
        Who sees this
      </h2>
      {listed ? (
        <p className="fp-prose">
          You're listed in Find a co-founder, so any signed-in founder can open your page. So can anyone you send the link to.
        </p>
      ) : (
        <>
          <p className="fp-prose">
            Founders you message can open your page, and so can anyone you send the link to. You're not listed in Find a co-founder.
          </p>
          {!me.hasValidCoFounderSubscription && (
            <button type="button" className="fp-text-btn fp-text-btn--blue" onClick={() => togglePremium()}>
              Get listed with Premium <IconArrow />
            </button>
          )}
        </>
      )}
    </section>
  )
}
